import {
  AlertCircle,
  Briefcase,
  Building,
  Camera,
  CheckCircle,
  Eye,
  EyeOff,
  Key,
  Mail,
  Save,
  Shield,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import useAuthStore from "../stores/authStore";
import { authAPI } from "../utils/api";

const ROLE_LABELS = {
  admin: "管理者",
  manager: "マネージャー",
  user: "一般ユーザー"
};

const ProfilePage = () => {
  const { user, updateUser } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [profileData, setProfileData] = useState({
    full_name: "",
    email: "",
    department: "",
    position: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  // ユーザー情報を初期化
  useEffect(() => {
    if (user) {
      setProfileData((prev) => ({
        ...prev,
        full_name: user.full_name || "",
        email: user.email || "",
        department: user.department || "",
        position: user.position || "",
      }));
    }
  }, [user]);

  // 現在のユーザー情報を取得
  const fetchCurrentUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authAPI.getMe();
      updateUser(response.data.user);
    } catch (err) {
      console.error("ユーザー情報取得エラー:", err);
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  // 初回ロード時にユーザー情報を取得
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await authAPI.updateProfile({
        full_name: profileData.full_name,
        email: profileData.email,
        department: profileData.department,
        position: profileData.position,
      });

      // ユーザー情報を更新
      updateUser({
        ...user,
        full_name: profileData.full_name,
        email: profileData.email,
        department: profileData.department,
        position: profileData.position,
      });

      setMessage({ type: "success", text: "プロフィールを更新しました" });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.error || "プロフィールの更新に失敗しました",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (profileData.new_password !== profileData.confirm_password) {
      setMessage({
        type: "error",
        text: "新しいパスワードと確認パスワードが一致しません",
      });
      return;
    }

    if (profileData.new_password.length < 6) {
      setMessage({
        type: "error",
        text: "新しいパスワードは6文字以上で入力してください",
      });
      return;
    }

    setPasswordLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await authAPI.changePassword({
        current_password: profileData.current_password,
        new_password: profileData.new_password,
      });

      // パスワードフィールドをクリア
      setProfileData((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));

      setMessage({ type: "success", text: "パスワードを更新しました" });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.error || "パスワードの更新に失敗しました",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            プロフィール情報を読み込み中...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">個人設定</h1>
        <p className="text-muted-foreground">
          アカウント情報とセキュリティ設定の管理
        </p>
      </div>

      {/* メッセージ表示 */}
      {message.text && (
        <div
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* プロフィール情報 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>プロフィール情報</span>
            </CardTitle>
            <CardDescription>個人情報とアカウント設定を更新</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              {/* アバター */}
              <div className="flex items-center space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
                  <AvatarFallback className="text-lg">
                    {user?.full_name?.charAt(0) ||
                      user?.username?.charAt(0) ||
                      "U"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                    disabled={true}
                  >
                    <Camera className="h-4 w-4" />
                    <span>アバターを変更</span>
                  </Button>
                </div>
              </div>

              {/* 氏名 */}
              <div className="space-y-2">
                <label htmlFor="full_name" className="text-sm font-medium">
                  氏名
                </label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={profileData.full_name}
                  onChange={handleInputChange}
                  placeholder="氏名を入力"
                  required
                />
              </div>

              {/* メールアドレス */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  メールアドレス
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={profileData.email}
                    onChange={handleInputChange}
                    placeholder="メールアドレスを入力"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* 部署 */}
              <div className="space-y-2">
                <label htmlFor="department" className="text-sm font-medium">
                  部署
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="department"
                    name="department"
                    value={profileData.department}
                    onChange={handleInputChange}
                    placeholder="部署を入力"
                    className="pl-10"
                  />
                </div>
              </div>

              {/* 役職 */}
              <div className="space-y-2">
                <label htmlFor="position" className="text-sm font-medium">
                  役職
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="position"
                    name="position"
                    value={profileData.position}
                    onChange={handleInputChange}
                    placeholder="役職を入力"
                    className="pl-10"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full flex items-center space-x-2"
                disabled={profileLoading}
              >
                {profileLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>
                  {profileLoading ? "更新中..." : "プロフィールを更新"}
                </span>
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* セキュリティ設定 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>セキュリティ設定</span>
            </CardTitle>
            <CardDescription>
              パスワードとセキュリティ設定の変更
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* 現在のパスワード */}
              <div className="space-y-2">
                <label
                  htmlFor="current_password"
                  className="text-sm font-medium"
                >
                  現在のパスワード
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showPassword ? "text" : "password"}
                    value={profileData.current_password}
                    onChange={handleInputChange}
                    placeholder="現在のパスワードを入力"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* 新しいパスワード */}
              <div className="space-y-2">
                <label htmlFor="new_password" className="text-sm font-medium">
                  新しいパスワード
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={profileData.new_password}
                    onChange={handleInputChange}
                    placeholder="新しいパスワードを入力"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* パスワード確認 */}
              <div className="space-y-2">
                <label
                  htmlFor="confirm_password"
                  className="text-sm font-medium"
                >
                  パスワード確認
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={profileData.confirm_password}
                    onChange={handleInputChange}
                    placeholder="パスワードを再入力"
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full flex items-center space-x-2"
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Shield className="h-4 w-4" />
                )}
                <span>
                  {passwordLoading ? "更新中..." : "パスワードを更新"}
                </span>
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* アカウント情報 */}
      <Card>
        <CardHeader>
          <CardTitle>アカウント情報</CardTitle>
          <CardDescription>アカウントの基本情報とログイン履歴</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                ユーザー名
              </p>
              <p className="text-sm">{user?.username}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                ロール
              </p>
              <p className="text-sm">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                アカウント作成日
              </p>
              <p className="text-sm">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString("ja-JP")
                  : "不明"}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                最終ログイン
              </p>
              <p className="text-sm">
                {user?.last_login
                  ? new Date(user.last_login).toLocaleString("ja-JP")
                  : "不明"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
